/**
 * repApi — dedicated axios instance for the Representative Portal.
 *
 * Strict isolation from the tenant `api` client:
 *   • Stores token under `agentus_rep_token` (NOT `agentos_token`).
 *   • On 401 redirects to `/representante/login` (NOT `/login`).
 *   • Has no interceptors related to credits / impersonation / etc.
 *
 * This separation guarantees a rep can never accidentally touch tenant
 * surface, and a tenant operator can never appear authenticated in the
 * rep portal even if both tabs are open.
 */
import axios from "axios";

function resolveBackendUrl() {
  const fromEnv = process.env.REACT_APP_BACKEND_URL;
  if (fromEnv && /^https?:\/\//i.test(fromEnv)) {
    return fromEnv.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.__BACKEND_URL__) {
    return window.__BACKEND_URL__.replace(/\/$/, "");
  }
  return "";
}

export const REP_TOKEN_KEY = "agentus_rep_token";
export const REP_USER_KEY = "agentus_rep_user";

const BACKEND_URL = resolveBackendUrl();

export const repApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

repApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(REP_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

repApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      localStorage.removeItem(REP_TOKEN_KEY);
      localStorage.removeItem(REP_USER_KEY);
      if (!window.location.pathname.startsWith("/representante/login")) {
        window.location.href = "/representante/login";
      }
    }
    return Promise.reject(err);
  }
);

export function setRepSession(token, rep) {
  localStorage.setItem(REP_TOKEN_KEY, token);
  localStorage.setItem(REP_USER_KEY, JSON.stringify(rep));
}

export function getRepSession() {
  const token = localStorage.getItem(REP_TOKEN_KEY);
  const rawUser = localStorage.getItem(REP_USER_KEY);
  if (!token || !rawUser) return null;
  try {
    return { token, rep: JSON.parse(rawUser) };
  } catch {
    return null;
  }
}

export function clearRepSession() {
  localStorage.removeItem(REP_TOKEN_KEY);
  localStorage.removeItem(REP_USER_KEY);
}
